import React from 'react';
import Cropper from 'react-cropper';
import Button from '../../button';
import Dialog from '../../dialog';
import { autobind } from 'core-decorators';
import { keydown, KEYS } from '../../../utils/keydown-decorator';
import ButtonToolbar from 'src/componentes/button-toolbar';
import Dragger from 'react-dropzone';
import LucideIcon from '../../lucide-icon';
import http from 'src/services/httpService';
import 'cropperjs/dist/cropper.css';
import './style/file.scss';
import { injectIntl } from 'react-intl';
import LoadingContainer from '../../loading-container';
import errorHandler from 'src/utils/error-handler';
import { enqueueSnackbar } from 'notistack';

@autobind
class SelectImage extends React.Component {
  static defaultProps = {
    tipoAnexo: null,
    subtitle: null,
    aspectRatio: undefined,
    maxSize: undefined,
    maxWidth: undefined,
    maxHeight: undefined,
    convertImageLimitToJpeg: false,
  };

  constructor(props) {
    super(props);

    this.cropperRef = React.createRef();

    this.state = {
      arquivo: null,
      isUploading: false,
    };
  }

  handleChange(files) {
    if (!files || files.length === 0) return;

    const f = files[0];
    if (!f.preview && typeof URL !== 'undefined' && URL.createObjectURL) {
      f.preview = URL.createObjectURL(f);
    }
    this.setState({ arquivo: f });
  }

  _crop() {}

  @keydown(KEYS.ESC)
  handleCancelClick() {
    this.refs.dialog.close();
  }

  _getLimitedCanvas(cropper) {
    const { maxWidth, maxHeight } = this.props;

    // Se maxWidth ou maxHeight foram fornecidos, aplica limitação
    const canvasOptions = {
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    };

    if (maxWidth !== undefined) {
      canvasOptions.maxWidth = maxWidth;
    }

    if (maxHeight !== undefined) {
      canvasOptions.maxHeight = maxHeight;
    }
    return cropper.getCroppedCanvas(canvasOptions);
  }

  // util: promisify toBlob
  _canvasToJpegBlob(canvas, quality = 1) {
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Falha ao gerar JPEG.'));
            resolve(blob);
          },
          'image/jpeg',
          quality
        );
      } catch (e) {
        reject(e);
      }
    });
  }

  // util: promisify toBlob - mantém formato original
  _canvasToOriginalBlob(canvas, originalFile) {
    return new Promise((resolve, reject) => {
      try {
        const originalType = originalFile.type || 'image/png';
        const isJpeg =
          originalType.includes('jpeg') || originalType.includes('jpg');

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Falha ao gerar imagem.'));
            resolve(blob);
          },
          originalType,
          isJpeg ? 1.0 : undefined
        );
      } catch (e) {
        reject(e);
      }
    });
  }

  async _toJpegFile(canvas, originalFile, quality = 0.8) {
    const blob = await this._canvasToJpegBlob(canvas, quality);
    const fileName =
      (originalFile?.name || 'foto').replace(/\.(png|jpe?g)$/i, '') + '.jpg';

    return new File([blob], fileName, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  }

  @keydown(KEYS.ENTER)
  async handleConfirmClick() {
    const cropper = this.cropperRef?.current?.cropper;

    if (
      !cropper ||
      typeof cropper.getCroppedCanvas === 'undefined' ||
      !this.state.arquivo
    ) {
      return;
    }

    this.setState({ isUploading: true });

    try {
      const canvas = this._getLimitedCanvas(cropper);
      const originalFile = this.state.arquivo;
      const { maxWidth, maxHeight, maxSize, convertImageLimitToJpeg } =
        this.props;
      const hasResizeLimit = maxWidth !== undefined && maxHeight !== undefined;

      let file;

      if (hasResizeLimit) {
        file = await this._toJpegFile(canvas, originalFile);
      } else {
        const fileBlob = await this._canvasToOriginalBlob(canvas, originalFile);
        file = new File([fileBlob], originalFile.name || 'foto', {
          type: originalFile.type || 'image/png',
          lastModified: Date.now(),
        });
      }

      const isPng =
        (originalFile.type || '').toLowerCase().includes('png') ||
        (originalFile.name || '').toLowerCase().endsWith('.png');

      if (
        convertImageLimitToJpeg &&
        !hasResizeLimit &&
        isPng &&
        maxSize &&
        file.size > maxSize
      ) {
        file = await this._toJpegFile(canvas, originalFile);
      }

      if (maxSize && file.size > maxSize) {
        this.setState({ isUploading: false });

        enqueueSnackbar(
          this.props.intl.formatMessage({
            id: 'imagemFicouAcimaDoTamanhoLimite',
          }),
          {
            variant: 'error',
          }
        );

        return;
      }

      const data = new FormData();
      data.append('tipoAnexo', this.props.tipoAnexo);
      data.append('idReferencia', this.props.idReferencia || '');
      data.append('file', file, file.name);

      const result = await http.post('ArquivoSistema/uploadArquivo', data);

      if (this.props.model && this.props.model.requestChange) {
        this.props.model.requestChange(result.data);
      }
      if (this.props.afterUpload && result.data.id) {
        this.props.afterUpload(result.data.id);
      }

      this.refs.dialog.close();
    } catch (error) {
      this.setState({ isUploading: false });
      errorHandler(error);
    }
  }

  render() {
    let { title, subtitle, intl, aspectRatio, ...other } = this.props;

    const actions = [
      <ButtonToolbar key="toolbar-img">
        <Button
          onClick={this.handleCancelClick}
          className="pull-left"
          leftIcon="close"
          color="secondary"
        >
          {intl.formatMessage({ id: 'label.fechar' })}
        </Button>
        <Button
          onClick={this.handleConfirmClick}
          leftIcon="content-save-outline"
          type="primary"
        >
          {intl.formatMessage({ id: 'label.salvarEFechar' })}
        </Button>
      </ButtonToolbar>,
    ];

    return (
      <Dialog
        {...other}
        ref="dialog"
        title={title}
        width={this.state.arquivo ? 1000 : 600}
        height={this.state.arquivo ? 700 : 450}
        actions={actions}
        padContent
        onRequestClose={this.handleCancelClick}
      >
        <LoadingContainer isLoading={this.state.isUploading}>
          {subtitle}
          <div className="pb-3">
            {!this.state.arquivo && (
              <Dragger
                multiple={false}
                accept={'.jpg, .jpeg, .png'}
                onDrop={this.handleChange}
                style={{
                  width: '100%',
                  height: 220,
                  border: '1px dashed #DDD',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  padding: 15,
                }}
              >
                <p>
                  {intl.formatMessage({
                    id: 'label.cliqueOuArrasteOArquivoNestaAreaParaAdicionalo',
                  })}
                </p>
                <LucideIcon icon="attachment" size={16} />
              </Dragger>
            )}
            {this.state.arquivo && (
              <Cropper
                style={{
                  height: 470,
                  width: '100%',
                }}
                aspectRatio={aspectRatio}
                preview=".img-preview"
                guides={false}
                src={this.state.arquivo && this.state.arquivo.preview}
                ref={this.cropperRef}
                crop={this._crop}
                autoCropArea={aspectRatio != undefined ? 1 : 0}
                viewMode={1}
              />
            )}
          </div>
        </LoadingContainer>
      </Dialog>
    );
  }
}

export default injectIntl(SelectImage);
